import { useCallback, useEffect, useRef, useState } from 'react';

import type { SurveyAnswerState } from '../buildAnswerPayload';
import type { SurveyQuestionData } from '../types';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';

import { QuestionType } from '@/constants/QuestionType';
import { useHydrationTracking } from '@/hooks/useDirtyState';

// Stable serialization so dirtiness is a comparison against the last-saved snapshot rather than a
// one-way flag — changing an answer and setting it back to the original reads as clean. Empty
// answers are dropped so undefined vs [] vs '' compare equal (matching the save payload).
const serializeAnswerState = (
    values: Record<string, string[]>,
    otherTexts: Record<string, string> = {},
): string => {
    const cleanedValues = Object.keys(values)
        .filter((key) => (values[key]?.length ?? 0) > 0)
        .sort()
        .map((key) => [key, values[key]]);
    const cleanedOtherTexts = Object.keys(otherTexts)
        .filter((key) => (otherTexts[key]?.length ?? 0) > 0)
        .sort()
        .map((key) => [key, otherTexts[key]]);
    return JSON.stringify({
        values: cleanedValues,
        otherTexts: cleanedOtherTexts,
    });
};

export const useSurveyAnswers = (
    cycleInstance: CycleInstance | undefined,
    surveyQuestions: SurveyQuestionData[],
) => {
    const [answerValues, setAnswerValues] = useState<Record<string, string[]>>({});
    const [otherTextValues, setOtherTextValues] = useState<Record<string, string>>({});
    // The answers as the server returned them, so autosave can tell them apart from an edit.
    const [loadedAnswers, setLoadedAnswers] = useState<SurveyAnswerState | undefined>(undefined);

    const { hydratedRef } = useHydrationTracking();
    // Snapshot of the last-saved (or hydrated) answers; dirtiness compares current against it.
    const baselineRef = useRef(serializeAnswerState({}, {}));

    // Rehydrate from saved answers
    useEffect(() => {
        if (hydratedRef.current) {
            return;
        }
        const savedAnswers = cycleInstance?.managerAssessmentAnswers ?? [];
        if (savedAnswers.length === 0 || surveyQuestions.length === 0) {
            return;
        }

        const questionTypeById = new Map(
            surveyQuestions.map((question) => [
                question.competencyId ?? question.reviewQuestion?.id,
                question.questionType,
            ]),
        );
        const questionOptionsById = new Map(
            surveyQuestions.map((question) => [
                question.competencyId ?? question.reviewQuestion?.id,
                question.reviewQuestion?.reviewQuestionOptions ?? [],
            ]),
        );
        const questionHasOther = new Map(
            surveyQuestions.map((question) => [
                question.competencyId ?? question.reviewQuestion?.id,
                question.reviewQuestion?.hasOtherOption ?? false,
            ]),
        );
        const nextValues: Record<string, string[]> = {};
        const nextOtherTexts: Record<string, string> = {};

        savedAnswers.forEach((answer) => {
            const questionId = answer.reviewQuestionId;
            const questionType = questionTypeById.get(questionId);
            if (!questionType) {
                return;
            }
            switch (questionType) {
                case QuestionType.PLAIN_TEXT:
                    nextValues[questionId] = answer.textValue ? [answer.textValue] : [];
                    break;
                case QuestionType.RATING_SCALE:
                    nextValues[questionId] =
                        answer.intValue !== null && answer.intValue !== undefined
                            ? [String(answer.intValue)]
                            : [];
                    break;
                case QuestionType.YES_NO:
                    nextValues[questionId] =
                        answer.boolValue === true
                            ? ['yes']
                            : answer.boolValue === false
                              ? ['no']
                              : [];
                    break;
                case QuestionType.EMOJI_CHOICE: {
                    // The Answer component uses 'emoji-{index}' identifiers (matching the
                    // option's position in reviewQuestionOptions) rather than real UUIDs.
                    // Convert the persisted option IDs back to that format.
                    const options = questionOptionsById.get(questionId) ?? [];
                    nextValues[questionId] =
                        answer.selectedOptions?.map((opt) => {
                            const index = options.findIndex(
                                (o) => o.id === opt.reviewQuestionOptionId,
                            );
                            return `emoji-${index}`;
                        }) ?? [];
                    break;
                }
                default:
                    // Checkbox / multiple-choice: the Answer widget matches on the option id
                    // (reviewQuestionOptionId), not the selection row's own id.
                    nextValues[questionId] =
                        answer.selectedOptions?.map((opt) => opt.reviewQuestionOptionId) ?? [];
                    // Restore "Other" text for questions that have the Other option enabled
                    if (questionHasOther.get(questionId) && answer.textValue) {
                        nextOtherTexts[questionId] = answer.textValue;
                    }
                    break;
            }
        });

        setAnswerValues(nextValues);
        setOtherTextValues(nextOtherTexts);
        setLoadedAnswers({ answerValues: nextValues, otherTextValues: nextOtherTexts });
        baselineRef.current = serializeAnswerState(nextValues, nextOtherTexts);
        hydratedRef.current = true;
    }, [cycleInstance, surveyQuestions, hydratedRef]);

    const handleAnswerChange = useCallback((questionId: string, values: string[]) => {
        setAnswerValues((prev) => ({ ...prev, [questionId]: values }));
    }, []);

    const handleOtherTextChange = useCallback((questionId: string, text: string) => {
        setOtherTextValues((prev) => ({ ...prev, [questionId]: text }));
    }, []);

    // Adopt the current answers as the new baseline after a successful save.
    const markSaved = useCallback(() => {
        baselineRef.current = serializeAnswerState(answerValues, otherTextValues);
    }, [answerValues, otherTextValues]);

    const isDirty = serializeAnswerState(answerValues, otherTextValues) !== baselineRef.current;

    return {
        answerValues,
        otherTextValues,
        loadedAnswers,
        isDirty,
        handleAnswerChange,
        handleOtherTextChange,
        markSaved,
    };
};
